import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import SpotsBadge from "@/components/landing/SpotsBadge";

const headlineLines = [
  { text: "CORTISOLO ZERO:", accent: true },
  { text: "ABBATTI L'ATTRITO BIOLOGICO,", accent: false },
  { text: "SBLOCCA L'ALTA PERFORMANCE FLAWLESS.", accent: false },
];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.18, duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const scrollToSection = (hash) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(hash, { offset: -72 });
  } else {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 18 });
  const sy = useSpring(my, { stiffness: 45, damping: 18 });
  const goldX = useTransform(sx, (v) => v * 56);
  const goldY = useTransform(sy, (v) => v * 40);
  const copperX = useTransform(sx, (v) => v * -44);
  const copperY = useTransform(sy, (v) => v * -32);

  const handleMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      onMouseMove={handleMouseMove}
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-20"
    >
      <motion.div
        aria-hidden="true"
        style={{ x: goldX, y: goldY }}
        className="pointer-events-none absolute -top-32 right-[8%] h-[420px] w-[420px] rounded-full bg-[#D4AF37]/10 blur-[140px]"
      />
      <motion.div
        aria-hidden="true"
        style={{ x: copperX, y: copperY }}
        className="pointer-events-none absolute bottom-[-10%] left-[-6%] h-[380px] w-[380px] rounded-full bg-[#B87333]/10 blur-[130px]"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 px-6 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.p
            custom={0.15}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            data-testid="hero-eyebrow"
            className="mb-8 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.35em] text-[#D4AF37]"
          >
            <span className="inline-block h-px w-12 bg-[#D4AF37]/60" />
            Il Metodo di Joe Fenix
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif-lux text-4xl leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            {headlineLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={lineVariants}
                  className={`block ${line.accent ? "text-[#D4AF37]" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            custom={1.1}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            data-testid="hero-subtitle"
            className="mt-8 max-w-xl text-base leading-relaxed text-[#A3A3A3] md:text-lg"
          >
            Il protocollo scientifico transdisciplinare ingegnerizzato per
            imprenditori, manager e leader per ottimizzare l'energia
            mitocondriale e azzerare il logoramento cellulare.
          </motion.p>

          <motion.div
            custom={1.35}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <a
              href="https://amazon.com/dp/B0HKFF979L"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-amazon-cta"
              className="inline-block border border-[#B87333] bg-[#B87333] px-8 py-4 text-center text-sm font-bold uppercase tracking-[0.18em] text-white shadow-lg transition-colors duration-300 hover:bg-[#D2853D] hover:shadow-[0_0_32px_rgba(184,115,51,0.35)]"
            >
              Acquista il Manuale su Amazon
            </a>
            <button
              type="button"
              onClick={() => scrollToSection("#il-metodo")}
              data-testid="hero-explore-cta"
              className="border border-[#D4AF37]/40 px-8 py-4 text-sm font-medium uppercase tracking-[0.18em] text-[#D4AF37] transition-colors duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              Esplora il Metodo
            </button>
          </motion.div>

          <SpotsBadge />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-center"
        >
          <div className="animate-float-soft relative">
            <div
              aria-hidden="true"
              className="animate-spin-slow absolute -inset-5 rounded-full border border-dashed border-[#D4AF37]/30"
            />
            <div
              aria-hidden="true"
              className="absolute -inset-12 rounded-full bg-[#D4AF37]/5 blur-2xl"
            />
            <div
              data-testid="author-photo-placeholder"
              className="relative h-52 w-52 overflow-hidden rounded-full border-2 border-[#D4AF37] bg-[#141414] shadow-[0_0_60px_rgba(212,175,55,0.15)] md:h-64 md:w-64"
            >
              <img
                src="/assets/joe-fenix.webp"
                alt="Joe Fenix — Fondatore del Metodo CORTISOLO ZERO"
                className="h-full w-full object-cover object-[50%_30%] scale-[1.35]"
              />
            </div>
          </div>
          <p
            data-testid="author-caption"
            className="mt-10 text-center text-xs font-semibold uppercase tracking-[0.35em] text-[#737373]"
          >
            Joe Fenix <span className="mx-2 text-[#B87333]">—</span> Fondatore del Metodo
          </p>
        </motion.div>
      </div>
    </section>
  );
}
